import React, { useRef } from "react";
import { EyeIcon, FileIcon, FileSearchIcon, Link2Icon, MailIcon, MessageSquareIcon, UploadIcon, BoxIcon } from "lucide-react";
import { InlinePopover } from "./InlinePopover";
import { documentCount } from "../../utils/conditions";
import { Condition } from "../../types/conditions";
interface RowActionsProps {
  condition: Condition;
  onToggleEmail: () => void;
  onToggleBorrowerVisible: () => void;
  onUpload: (fileName: string) => void;
  onLinkDocuments: () => void;
  onComments: () => void;
}
const iconBtn = 'relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';
const offTone = 'text-slate-400 hover:bg-slate-100 hover:text-slate-700';
const onTone = 'bg-brand/10 text-brand hover:bg-brand/15';
export function RowActions({
  condition,
  onToggleEmail,
  onToggleBorrowerVisible,
  onUpload,
  onLinkDocuments,
  onComments
}: RowActionsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const docs = documentCount(condition);
  const comments = condition.comments.length;
  return <div className="flex items-center justify-end gap-0.5" data-id="element-270" data-mp="12wnn7e1lll4g">
      <input ref={fileRef} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={e => {
      const file = e.target.files?.[0];
      if (file) onUpload(file.name);
      e.target.value = '';
    }} data-id="element-271" data-mp="p6afncsmc8fn" />

      <Tooltip label={condition.includeInEmail ? 'Included in email' : 'Include in email'} data-id="element-272" data-mp="f7l6q9ip9ybj">
        <button type="button" onClick={onToggleEmail} aria-pressed={condition.includeInEmail} aria-label="Include in email" className={`${iconBtn} ${condition.includeInEmail ? onTone : offTone}`} data-id="element-273" data-mp="k7qtpce0ywna">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-274" data-mp="a8i22q6mtu43" />
        </button>
      </Tooltip>

      <Tooltip label={condition.borrowerVisible ? 'Borrower can see' : 'Hidden from borrower'} data-id="element-275" data-mp="20lqsul51qi9h">
        <button type="button" onClick={onToggleBorrowerVisible} aria-pressed={condition.borrowerVisible} aria-label="Can borrower see" className={`${iconBtn} ${condition.borrowerVisible ? onTone : offTone}`} data-id="element-276" data-mp="24msqixjbem9c">
          <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-277" data-mp="38kemzz0lwoyj" />
        </button>
      </Tooltip>

      <Tooltip label="Documents" data-id="element-278" data-mp="34j1k9xst91vu">
        <InlinePopover ariaLabel={`Documents for ${condition.name}${docs ? ` (${docs})` : ''}`} align="right" triggerClassName={`${iconBtn} ${offTone}`} trigger={<>
              <FileIcon className="h-4 w-4" aria-hidden="true" data-id="element-280" data-mp="5mh5njdpxyo" />
              <Badge count={docs} data-id="element-281" data-mp="6xmvswdxfsf" />
            </>} data-id="element-279" data-mp="34k48fxyr0q82">
          {close => <div role="menu" className="w-64 rounded-md border border-slate-200 bg-white py-1 shadow-lg" data-id="element-282" data-mp="3sa6x043gfe1x">
              <MenuItem icon={FileSearchIcon} title={`Review documents${docs ? ` (${docs})` : ''}`} hint={docs ? 'Open attached files' : 'Nothing attached yet'} disabled={docs === 0} onClick={close} data-id="element-283" data-mp="3ngm76zc0lqdt" />
              <div className="my-1 border-t border-slate-100" data-id="element-284" data-mp="jun7ng5wmfs5" />
              <MenuItem icon={UploadIcon} title="Upload new file" hint="From your computer" onClick={() => {
            close();
            fileRef.current?.click();
          }} data-id="element-285" data-mp="f1u1qoyp91eh" />
              <MenuItem icon={Link2Icon} title="Link existing document" hint="Already in the loan file" onClick={() => {
            close();
            onLinkDocuments();
          }} data-id="element-286" data-mp="rcczzgkxjzed" />
            </div>}
        </InlinePopover>
      </Tooltip>

      <Tooltip label="Activity & comments" data-id="element-287" data-mp="10e2p2frcam8z">
        <button type="button" onClick={onComments} aria-label={`Activity and comments${comments ? ` (${comments} comments)` : ''}`} className={`${iconBtn} ${offTone}`} data-id="element-288" data-mp="15ce0guxa94tb">
          <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="1a9mkz25wlkxq" />
          <Badge count={comments} data-id="element-290" data-mp="1f38mpzaj44er" />
        </button>
      </Tooltip>
    </div>;
}
function Badge({
  count
}: {
  count: number;
}) {
  if (!count) return null;
  return <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-brand px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white" data-id="element-291" data-mp="1o2vssye7dye5">
      {count}
    </span>;
}
function Tooltip({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="group/action relative" data-id="element-292" data-mp="1fd5bbpzbxpjo">
      {children}
      <span role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/action:opacity-100" data-id="element-293" data-mp="1bcv306o2en1n">
        {label}
      </span>
    </div>;
}
interface MenuItemProps {
  icon: BoxIcon;
  title: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
}
function MenuItem({
  icon: Icon,
  title,
  hint,
  onClick,
  disabled
}: MenuItemProps) {
  return <button type="button" role="menuitem" onClick={onClick} disabled={disabled} className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-294" data-mp="16fuu7nztz66u">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-295" data-mp="2ctuafbwwadpd" />
      <span data-id="element-296" data-mp="3urz5ktwria42">
        <span className="block text-sm font-medium text-slate-800" data-id="element-297" data-mp="3urh5fm6zbbqv">{title}</span>
        <span className="block text-xs text-slate-500" data-id="element-298" data-mp="3m7iy4i71fm0">{hint}</span>
      </span>
    </button>;
}